import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import { convertibleFromObject } from 'Modules/converter/index';
import { correctionLevelToParam } from 'Modules/serverApi/common/flags';

import { facetsToConvertible } from './common/convert';
import { asCmError, GET, GETBACKUP } from './common/httpClient';
import ResponseValidationError from './common/responseValidationError';
import { isAutocompleteItemShape } from './common/validators';
import { AUTOCOMPLETE_ENDPOINT } from './endpoints';
import type { AutocompleteModeFlag, SearchRequest, ServerModel } from './types';

const {
  autocomplete: { requestDefaults, order },
} = requestConfig;

export const AUTOCOMPLETE_QUERY_KEY = 'Type' as const;

const autocompleteTypeMap = {
  text: ['Freetext', 'PopularSearch'],
  product: ['Product'],
  facetValue: ['Category'],
} as const satisfies Record<string, readonly AutocompleteModeFlag[]>;

let invalidAutocompleteResponseLogged = false;

type AutocompleteTypeKey = keyof typeof autocompleteTypeMap;

function isAutocompleteTypeKey(type: string): type is AutocompleteTypeKey {
  return type in autocompleteTypeMap;
}

export default async function autocomplete(
  request: SearchRequest,
  abortSignal?: AbortSignal,
): Promise<ServerModel.AutocompleteResponse> {
  const defaults = requestDefaults ?? {};
  const extra = {
    ...(defaults.extra ?? {}),
    ...(request.extra ?? {}),
  };
  const params = {
    catalog: request.catalog ?? defaults.catalog ?? '',
    query: encodeURIComponent(request.query ?? defaults.query ?? ''),
    [AUTOCOMPLETE_QUERY_KEY]: getAutocompleteTypes(),
    querycorrectionlevel: correctionLevelToParam(
      request.queryCorrectionLevel,
      defaults.queryCorrectionLevel,
      '',
    ),
    facet: facetsToConvertible(request, defaults),
    extra: await convertibleFromObject(extra),
  };

  try {
    return getAutocompleteResponse(await GET(AUTOCOMPLETE_ENDPOINT, params, { signal: abortSignal }));
  } catch (primaryErr) {
    const pe = asCmError(primaryErr);
    if (pe.type !== CustomErrorType.requestAborted) {
      console.warn(`The autocomplete request failed: ${pe.message}`);
    }

    if (pe.status === 429) {
      throw primaryErr;
    }

    try {
      const backupResponse = getAutocompleteResponse(
        await GETBACKUP(AUTOCOMPLETE_ENDPOINT, params, {
          signal: abortSignal,
        }),
      );
      if (pe.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({ endpoint: AUTOCOMPLETE_ENDPOINT, scenario: 'backup_used', primaryError: pe });
      }
      return backupResponse;
    } catch (backupErr) {
      const be = asCmError(backupErr);
      if (pe.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({
          endpoint: AUTOCOMPLETE_ENDPOINT,
          scenario: 'backup_failed',
          primaryError: pe,
          backupError: be,
        });
      }
      throw backupErr;
    }
  }
}

function getAutocompleteTypes(): AutocompleteModeFlag[] | null {
  return order.length ? order.flatMap(getAutocompleteTypeNames) : null;
}

function getAutocompleteTypeNames(type: string): readonly AutocompleteModeFlag[] {
  return isAutocompleteTypeKey(type) ? autocompleteTypeMap[type] : [];
}

function getAutocompleteResponse(value: unknown): ServerModel.AutocompleteResponse {
  if (!Array.isArray(value)) {
    logInvalidAutocompleteResponse();
    return [];
  }

  const response = value.filter(isAutocompleteItemShape);
  if (response.length !== value.length) {
    logInvalidAutocompleteResponse();
  }
  return response;
}

function logInvalidAutocompleteResponse(): void {
  if (invalidAutocompleteResponseLogged) {
    return;
  }
  invalidAutocompleteResponseLogged = true;
  logNetworkRequest({
    endpoint: AUTOCOMPLETE_ENDPOINT,
    scenario: 'request_error',
    primaryError: new ResponseValidationError('Autocomplete response contained an unknown item Type.'),
  });
}
